// Связка чипов размера и вариантов на карточке товара.
//
// У большинства товаров варианты разложены по размерам (XS…XL — каждый свой
// вариант со своим остатком). Чип размера и миниатюра варианта раньше жили
// отдельно: покупатель выбирал «M», а в корзину уходил первый вариант «XS».

type SizedVariant = { id: string; size?: string | null; color?: string | null; stock: number };

/** Вариант под выбранный размер: сначала того же цвета и в наличии, потом любой в наличии, потом любой. */
export function resolveSizePick<V extends SizedVariant>(
	variants: V[],
	size: string,
	color?: string | null,
): V | undefined {
	const bySize = variants.filter((v) => v.size === size);
	if (bySize.length === 0) return undefined;
	return (
		bySize.find((v) => v.stock > 0 && (!color || v.color === color)) ??
		bySize.find((v) => v.stock > 0) ??
		bySize[0]
	);
}

/** Остаток по размеру (сумма по вариантам). Размеров вне вариантов в карте нет. */
export function sizeAvailability(variants: SizedVariant[]): Map<string, number> {
	const out = new Map<string, number>();
	for (const v of variants) {
		if (!v.size) continue;
		out.set(v.size, (out.get(v.size) ?? 0) + Math.max(0, v.stock));
	}
	return out;
}

/** Вариант с мерками «Таблицы размеров» — их считает шлюз по сетке категории. */
type MeasuredVariant = SizedVariant & {
	sizeLabel?: string;
	sizeSecondaryLabel?: string;
	sizeChest?: string;
	sizeWaist?: string;
	sizeHips?: string;
};

export type SizeTableRow = {
	/** Размер варианта как на чипе — по нему строка выбирает размер. */
	size: string;
	label: string;
	secondaryLabel?: string;
	/** Обхваты сетки категории, см; "" — у размера их нет. */
	chest: string;
	waist: string;
	hips: string;
	/** Свои мерки продавца по столбцам `SizeMeasures.columns`; "" — не указано. */
	extra: string[];
	available: boolean;
};

/** Свои мерки продавца (длина рукава, спинки…) — столбцы и значения по размеру. */
export type SizeMeasures = { columns: string[]; rows: Record<string, string[]> };

/**
 * Строки «Таблицы размеров» — как в приложении (ProductScreen.sizeTableRows):
 * размеры выбранного цвета, у которых шлюз отдал все три мерки (обхват груди,
 * талии, бёдер, см). С 19.10.2026 к ним добавляются свои мерки продавца
 * (блок 15.10 п. 9): размер попадает в таблицу, если у него есть обхваты сетки
 * ИЛИ хоть одна своя мерка — у обуви и детской ростовки обхватов нет, а длина
 * стельки есть. Один размер — одна строка; в наличии, если хоть один вариант
 * размера есть.
 */
export function sizeTableRows(variants: MeasuredVariant[], measures?: SizeMeasures | null): SizeTableRow[] {
	const columns = measures?.columns ?? [];
	const rows = new Map<string, SizeTableRow>();
	for (const v of variants) {
		const size = v.size?.trim();
		if (!size) continue;
		const body = Boolean(v.sizeChest && v.sizeWaist && v.sizeHips);
		const own = columns.map((_, index) => measures?.rows[size]?.[index]?.trim() ?? "");
		if (!body && !own.some(Boolean)) continue;
		const prev = rows.get(size);
		if (prev) {
			prev.available ||= v.stock > 0;
			continue;
		}
		rows.set(size, {
			size,
			label: v.sizeLabel?.trim() || size,
			secondaryLabel: v.sizeSecondaryLabel?.trim() || undefined,
			chest: body ? (v.sizeChest ?? "") : "",
			waist: body ? (v.sizeWaist ?? "") : "",
			hips: body ? (v.sizeHips ?? "") : "",
			extra: own,
			available: v.stock > 0,
		});
	}
	return [...rows.values()];
}

/** Подписи плитки размера: крупная строка и серая под ней. */
export type SizeChipLabel = { main: string; sub?: string };

/** «One size», «Универсальный», «Единый» — размер без шкалы. */
const ONE_SIZE = /^(one\s*-?\s*size|os|универсальный|единый(\s+размер)?|безразмерный)$/i;
export const isOneSize = (value: string): boolean => ONE_SIZE.test(value.trim());

/**
 * Что печатать на плитке размера (Айталы 21.10, photo_114/117): сверху
 * российский размер, под ним международный — «42 / XS»; у единого размера
 * сверху «One size», под ним диапазон — «One size / 44-54».
 *
 * Пару считает шлюз по размерной сетке категории (`sizeLabel` и
 * `sizeSecondaryLabel` варианта, `api-gateway/src/data/sizeSystem.ts`, женская
 * XS = 42, мужская M = 48), своя таблица витрине не нужна. Российский размер,
 * который продавец вписал сам (`options.russianSize`), шлюз ставит в
 * `sizeLabel`, а размер продавца — во вторую строку; у единого размера это
 * дало бы «44-54 / One size», поэтому строки меняются местами.
 *
 * Без подписей шлюза (размер вне вариантов, старый ответ) — запасной путь
 * «42/XS» через косую черту, иначе одна строка как есть.
 */
export function sizeChipLabel(
	size: string,
	variants: { size?: string | null; sizeLabel?: string; sizeSecondaryLabel?: string }[],
): SizeChipLabel {
	const labelled = variants.find((v) => v.size === size && v.sizeLabel?.trim());
	const [rawMain = size, rawSub] = labelled
		? [labelled.sizeLabel?.trim() ?? size, labelled.sizeSecondaryLabel?.trim()]
		: size.split("/").map((part) => part.trim());
	let main = rawMain || size.trim();
	let sub = rawSub || undefined;
	if (sub && isOneSize(sub) && !isOneSize(main)) [main, sub] = [sub, main];
	if (sub && sub.toLowerCase() === main.toLowerCase()) sub = undefined;
	return { main, sub };
}
